import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CreateUpdatePortalThemeRequest } from '@app/@shared/models';
import { ApplicationProfile, PortalTheme } from '@app/@shared/models/application-profile.model';
import { ApplicationProfileService } from '@app/@shared/services/application-profile.service';
import { AuthenticationService } from '@app/@shared/services/authentication.service';
import { PortalThemeService } from '@app/@shared/services/portal-theme.service';
import { EPTableConfig, FONT_AWESOME_ICONS, ViewCardConfig, EPTableColumns } from '@ep/shared';
import { MessageService } from 'primeng/api';

export enum ColorsEnum {
  LIGHT_BLUE = '#30a0e6',
  MOSSY_GREEN = '#68ac3c',
  BLUE_GREY = '#3a5171',
  DOC_PURPLE = '#ad6dd1',
  LIGHT_GRAY = '#7e7e7e',
}

export interface OverviewPanelData {
  header: string;
  headerColor?: string;
  subHeader?: string;
  subHeaderColor?: string;
  body: string;
  bodyColor?: string;
  subBody?: string;
  footer: string;
  subFooter?: string;
  footerColor?: ColorsEnum;
}

export interface ConsumerTableDisplay {
  ConsumerId: number;
  NameFirst: string;
  NameLast: string;
  LastSeenDateTime: Date | string | null;
}

@Component({
  selector: 'app-theme',
  templateUrl: './theme.component.html',
  styleUrls: ['./theme.component.scss'],
})
export class ThemeComponent implements OnInit, OnDestroy {
  private readonly applicationProfileService = inject(ApplicationProfileService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly portalThemeService = inject(PortalThemeService);
  private readonly messageService = inject(MessageService);

  icons = FONT_AWESOME_ICONS;
  protected advancedView: boolean = false;
  protected lastView: string = 'basic';
  protected customLogoSrc: string | null = null;
  protected customReducedLogoSrc: string | null = null;
  protected customFaviconSrc: string | null = null;

  protected customLogoSrcBkp: string | null = null;
  protected customReducedLogoSrcBkp: string | null = null;
  protected customFaviconSrcBkp: string | null = null;
  protected customLogoUploaded: boolean = false;
  protected customReducedLogoUploaded: boolean = false;
  protected customFaviconUploaded: boolean = false;

  protected showPendingImagesWarning: boolean = false;

  applicationProfile!: ApplicationProfile;

  // Utils
  // --------------------------------------------------
  ngOnInit() {
    this.applicationProfileService.applicationProfile$.subscribe((profile) => {
      this.applicationProfile = profile;
      if (profile.Representative?.PortalTheme?.Theme) {
        this.theme = JSON.parse(JSON.stringify(profile.Representative!.PortalTheme!.Theme));

        this.customLogoSrc =
          profile.Representative?.PortalTheme?.PrimaryLogo ||
          profile.Representative?.PortalTheme?.PendingPrimaryLogo ||
          '/assets/enroll-and-pay-logo.png';
        this.customReducedLogoSrc =
          profile.Representative?.PortalTheme?.SecondaryLogo ||
          profile.Representative?.PortalTheme?.PendingSecondaryLogo ||
          '/assets/enroll-and-pay-logo.png';
        this.customFaviconSrc =
          profile.Representative?.PortalTheme?.FavIcon ||
          profile.Representative?.PortalTheme?.PendingFavIcon ||
          '/assets/enroll-and-pay-logo.png';

        this.showPendingImagesWarning =
          profile.Representative?.PortalTheme?.PendingPrimaryLogo !== null ||
          profile.Representative?.PortalTheme?.PendingSecondaryLogo !== null ||
          profile.Representative?.PortalTheme?.PendingFavIcon !== null;

        this.customLogoSrcBkp = this.customLogoSrc;
        this.customReducedLogoSrcBkp = this.customReducedLogoSrc;
        this.customFaviconSrcBkp = this.customFaviconSrc;
      }
    });

    this.setDashboardConfigs();
    this.initializeTableConfig();
    this.applyThemeCssProperties();
  }

  ngOnDestroy() {
    // Clear all animation timeouts
    this.animationTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    this.animationTimeouts.clear();

    // Clear active animations tracking
    this.activeAnimations.clear();

    // Remove any active animations
    document.querySelectorAll('.hue-rotate-animation').forEach((element) => {
      element.classList.remove('hue-rotate-animation');
    });
    this.stopAnimation = true;
    this.currentAnimationProperty = null;
  }

  protected saveTheme() {
    var themeRequest: CreateUpdatePortalThemeRequest = {
      theme: JSON.stringify(this.theme),
      primaryLogo: this.customLogoUploaded ? this.customLogoSrc || '' : '',
      secondaryLogo: this.customReducedLogoUploaded ? this.customReducedLogoSrc || '' : '',
      favIcon: this.customFaviconUploaded ? this.customFaviconSrc || '' : '',
    };

    this.showPendingImagesWarning =
      this.customLogoUploaded || this.customReducedLogoUploaded || this.customFaviconUploaded;

    this.authenticationService
      .updateMerchantPortalTheme(this.applicationProfile.Representative.CoalitionId, themeRequest)
      .pipe()
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'SUCCESS: Update Theme',
            detail: 'Successfully updated the theme',
          });
          this.customLogoUploaded = false;
          this.customReducedLogoUploaded = false;
          this.customFaviconUploaded = false;
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'ERROR: Update Theme',
            detail: 'Failed to update the theme',
          });
        },
      });
  }

  private applyThemeCssProperties(overrideByPrimary?: boolean): void {
    this.portalThemeService.applyTheme(this.theme, {
      advancedView: this.advancedView,
      overrideByPrimary,
    });
  }

  protected changeView() {
    this.lastView = this.advancedView ? 'advanced' : 'basic';
    this.advancedView = !this.advancedView;
    this.applyLiveColor();
  }

  protected stopAnimation: boolean = false;
  private currentAnimationProperty: string | null = null;
  private currentAnimationOriginalValue: any = null;
  private animationTimeouts: Map<string, number> = new Map();
  private activeAnimations: Set<string> = new Set();
  protected isColorPickerOpened: boolean = false;

  protected animateColorTable(propertyPath: string = 'primary') {
    // Stop any currently running animation and restore its original value
    if (this.currentAnimationProperty && this.currentAnimationOriginalValue !== null) {
      this.stopAnimation = true;
      this.setNestedProperty(this.theme, this.currentAnimationProperty, this.currentAnimationOriginalValue);
      this.applyLiveColor();
    }

    // Store the original value and current animation property
    const original = this.getNestedProperty(this.theme, propertyPath);
    this.currentAnimationProperty = propertyPath;
    this.currentAnimationOriginalValue = original;
    this.stopAnimation = false; // Reset stop flag for new animation

    this.setNestedProperty(this.theme, propertyPath, '#07c8f9');
    this.applyLiveColor();

    setTimeout(() => {
      if (this.stopAnimation || this.currentAnimationProperty !== propertyPath) {
        this.setNestedProperty(this.theme, propertyPath, original);
        this.applyLiveColor();
        return;
      }
      this.setNestedProperty(this.theme, propertyPath, '#09a6f3');
      this.applyLiveColor();
    }, 200);

    setTimeout(() => {
      if (this.stopAnimation || this.currentAnimationProperty !== propertyPath) {
        this.setNestedProperty(this.theme, propertyPath, original);
        this.applyLiveColor();
        return;
      }
      this.setNestedProperty(this.theme, propertyPath, '#0a85ed');
      this.applyLiveColor();
    }, 400);

    setTimeout(() => {
      if (this.stopAnimation || this.currentAnimationProperty !== propertyPath) {
        this.setNestedProperty(this.theme, propertyPath, original);
        this.applyLiveColor();
        return;
      }
      this.setNestedProperty(this.theme, propertyPath, '#0c63e7');
      this.applyLiveColor();
    }, 600);

    setTimeout(() => {
      if (this.stopAnimation || this.currentAnimationProperty !== propertyPath) {
        this.setNestedProperty(this.theme, propertyPath, original);
        this.applyLiveColor();
        return;
      }
      this.setNestedProperty(this.theme, propertyPath, '#0d41e1');
      this.applyLiveColor();
    }, 800);

    setTimeout(() => {
      if (this.stopAnimation || this.currentAnimationProperty !== propertyPath) {
        this.setNestedProperty(this.theme, propertyPath, original);
        this.applyLiveColor();
        return;
      }
      this.setNestedProperty(this.theme, propertyPath, original);
      this.applyLiveColor();

      // Clear animation tracking when complete
      if (this.currentAnimationProperty === propertyPath) {
        this.currentAnimationProperty = null;
        this.currentAnimationOriginalValue = null;
      }
    }, 800);
  }

  protected animateColor(propertyPath: string = 'primary') {
    if (this.isColorPickerOpened) {
      return;
    }

    // Clear any existing timeout for this property
    const existingTimeout = this.animationTimeouts.get(propertyPath);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Reset stopAnimation flag
    this.stopAnimation = false;

    // Get the target elements based on the property path
    const targetElements = this.getAnimationTargets(propertyPath);

    if (targetElements.length === 0) {
      return;
    }

    // Determine which animation class to use based on the property
    const animationClass = this.getAnimationClass(propertyPath);

    // Add animation class to all target elements
    targetElements.forEach((element, index) => {
      if (element.classList.contains('p-tabview-nav-link') && targetElements.length === 2) {
        element.classList.add('background-animation');
        return;
      } else if (element.classList.contains('p-tabview-nav-link') && animationClass === 'background-animation') {
        element.classList.add('border-animation');
        return;
      }

      if (element.classList.contains('header-icon') || element.tagName === 'svg') {
        element.classList.add('menu-animation');
        return;
      }

      element.classList.add(animationClass);
    });

    // Track this animation as active
    this.activeAnimations.add(propertyPath);

    // Set up a timeout to stop the animation
    const timeoutId = window.setTimeout(() => {
      if (!this.stopAnimation) {
        targetElements.forEach((element) => {
          element.classList.remove(animationClass);
        });
      }
      this.animationTimeouts.delete(propertyPath);
      this.activeAnimations.delete(propertyPath);
    }, 3000); // Run animation for 3 seconds

    this.animationTimeouts.set(propertyPath, timeoutId);

    // Store current animation info
    this.currentAnimationProperty = propertyPath;

    // Check for stopAnimation flag periodically
    this.checkStopAnimation(targetElements, propertyPath, animationClass);
  }

  protected stopAllAnimations() {
    this.stopAnimation = true;

    // Stop all active animations immediately
    this.activeAnimations.forEach((propertyPath) => {
      const targetElements = this.getAnimationTargets(propertyPath);
      const animationClass = this.getAnimationClass(propertyPath);
      targetElements.forEach((element) => {
        if (element.classList.contains('p-tabview-nav-link') && animationClass === 'background-animation') {
          element.classList.remove('border-animation');
        }
        if (element.classList.contains('header-icon') || element.tagName === 'svg') {
          element.classList.remove('menu-animation');
          return;
        }
        element.classList.remove(animationClass);
      });

      // Clear the timeout
      const timeoutId = this.animationTimeouts.get(propertyPath);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.animationTimeouts.delete(propertyPath);
      }
    });

    // Clear tracking sets
    this.activeAnimations.clear();
    this.currentAnimationProperty = null;
  }

  private getAnimationClass(propertyPath: string): string {
    switch (propertyPath) {
      case 'table.subHeaderTextColor':
        return 'color-cycle';

      case 'dashboard.sales':
      case 'dashboard.loyalty':
      case 'dashboard.reviews':
      case 'dashboard.rewards':
      case 'form.backgroundColor':
        return 'hue-rotate-animation';

      case 'defaultTextColor':
      case 'dashboard.coloredCardTextColor':
      case 'sidebar.textColor':
      case 'table.header.textColor':
      case 'form.header.textColor':
      case 'table.body.textColor':
      case 'header.textColor':
      case 'dashboard.headerTextColor':
      case 'form.header.itemsTextColor':
        return 'color-cycle';

      default:
        return 'background-animation';
    }
  }

  private checkStopAnimation(targetElements: Element[], propertyPath: string, animationClass: string) {
    if (this.stopAnimation) {
      // Stop all animations, not just the current one
      this.stopAllAnimations();
    } else if (!this.stopAnimation && this.activeAnimations.has(propertyPath)) {
      // Continue checking
      setTimeout(() => this.checkStopAnimation(targetElements, propertyPath, animationClass), 100);
    }
  }

  private getAnimationTargets(propertyPath: string): Element[] {
    const elements: Element[] = [];

    switch (propertyPath) {
      case 'primary':
        // Animate all elements that use the primary color
        elements.push(...Array.from(document.querySelectorAll('.nav-link.activeMenu')));
        elements.push(...Array.from(document.querySelectorAll('.p-button')));
        elements.push(...Array.from(document.querySelectorAll('.table-header-wrapper')));
        elements.push(...Array.from(document.querySelectorAll('.form-card-header')));
        elements.push(
          ...Array.from(document.querySelectorAll('.p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link'))
        );
        elements.push(...Array.from(document.querySelectorAll('p-paginator .p-paginator-pages .p-paginator-element')));

        elements.push(...Array.from(document.querySelectorAll('p-table .p-datatable .p-datatable-header')));
        elements.push(...Array.from(document.querySelectorAll('.p-datatable-header')));

        elements.push(...Array.from(document.querySelectorAll('.p-accordion-header-link')));

        elements.push(...Array.from(document.querySelectorAll('.header-icon')));

        elements.push(
          ...Array.from(
            document.querySelectorAll('.p-datatable .p-datatable-thead > tr > th p-sorticon sortalticon svg')
          )
        );

        break;

      // Header
      // --------------------------------------------------

      case 'header.backgroundColor':
        elements.push(...Array.from(document.querySelectorAll('.header-wrapper')));
        break;

      // Sidebar
      // --------------------------------------------------
      case 'sidebar.backgroundColor.primary':
        elements.push(...Array.from(document.querySelectorAll('.side-navigation-bar')));
        break;

      case 'backgroundColor':
        elements.push(...Array.from(document.querySelectorAll('.main-content')));
        elements.push(...Array.from(document.querySelectorAll('.p-tabview .p-tabview-nav li .p-tabview-nav-link')));
        elements.push(...Array.from(document.querySelectorAll('p-tabview .p-tabview-nav')));
        elements.push(...Array.from(document.querySelectorAll('p-tabview .p-tabview-panels')));
        elements.push(...Array.from(document.querySelectorAll('.dashboard-preview')));
        elements.push(...Array.from(document.querySelectorAll('.loyalty-overview-panel-column-data')));

        break;

      case 'sidebar.textColor':
        elements.push(...Array.from(document.querySelectorAll('.nav-link-content')));
        elements.push(...Array.from(document.querySelectorAll('.theme-text-color')));
        break;

      case 'sidebar.logoBackgroundColor':
        elements.push(...Array.from(document.querySelectorAll('.side-navigation-bar-header')));
        break;

      case 'sidebar.backgroundColor.secondary':
        elements.push(...Array.from(document.querySelectorAll('.nav-link:hover')));
        break;

      case 'sidebar.backgroundColor.active':
        elements.push(...Array.from(document.querySelectorAll('.nav-link.activeMenu')));
        break;

      case 'sidebar.backgroundColor.dropdown':
        elements.push(...Array.from(document.querySelectorAll('.p-accordion-header-link')));
        break;

      case 'header.itemsColor':
        elements.push(...Array.from(document.querySelectorAll('.header-item')));
        elements.push(...Array.from(document.querySelectorAll('.header-icon')));
        break;

      case 'header.textColor':
        elements.push(...Array.from(document.querySelectorAll('.p-button-label')));
        elements.push(...Array.from(document.querySelectorAll('.p-button-icon.p-button-icon-left.pi.pi-angle-down')));
        break;

      // Dashboard
      // --------------------------------------------------
      case 'dashboard.sales':
        const salesCards = Array.from(document.querySelectorAll('ep-view-card')).filter(
          (card) => card.querySelector('.view-card-subheader')?.textContent?.trim() === 'SALES'
        );
        elements.push(...salesCards);

        elements.push(...Array.from(document.querySelectorAll('.overview-panel-body.text-overflow.sales-overview-01')));
        break;

      case 'dashboard.loyalty':
        const loyaltyCards = Array.from(document.querySelectorAll('ep-view-card')).filter(
          (card) => card.querySelector('.view-card-subheader')?.textContent?.trim() === 'LOYALTY'
        );
        elements.push(...loyaltyCards);
        elements.push(...Array.from(document.querySelectorAll('.overview-panel-body.text-overflow.sales-overview-02')));
        break;

      case 'dashboard.rewards':
        const rewardsCards = Array.from(document.querySelectorAll('ep-view-card')).filter(
          (card) => card.querySelector('.view-card-subheader')?.textContent?.trim() === 'REWARDS'
        );
        elements.push(...rewardsCards);
        elements.push(...Array.from(document.querySelectorAll('.overview-panel-body.text-overflow.sales-overview-03')));
        break;

      case 'dashboard.reviews':
        const reviewsCards = Array.from(document.querySelectorAll('ep-view-card')).filter(
          (card) => card.querySelector('.view-card-subheader')?.textContent?.trim() === 'REVIEWS'
        );
        elements.push(...reviewsCards);
        break;

      case 'dashboard.headerBackgroundColor':
        // Target the PrimeNG tabview navigation and panel header elements
        elements.push(...Array.from(document.querySelectorAll('.p-panel-header')));
        elements.push(
          ...Array.from(document.querySelectorAll('.p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link'))
        );

        // Fallback: target the container elements if PrimeNG elements aren't found
        if (elements.length === 0) {
          elements.push(...Array.from(document.querySelectorAll('p-tabview')));
          elements.push(...Array.from(document.querySelectorAll('p-panel')));
          elements.push(...Array.from(document.querySelectorAll('.full-width')));
          elements.push(...Array.from(document.querySelectorAll('.dashboard-overview-panel-wrapper')));
        }
        break;

      case 'dashboard.coloredCardTextColor':
        elements.push(...Array.from(document.querySelectorAll('.p-card .p-card-body')));
        elements.push(...Array.from(document.querySelectorAll('.p-card-content')));
        elements.push(...Array.from(document.querySelectorAll('.view-card-content')));
        break;

      case 'dashboard.cardBackgroundColor':
        elements.push(...Array.from(document.querySelectorAll('.p-panel-content')));
        elements.push(...Array.from(document.querySelectorAll('.overview-panel-row-data')));
        break;

      case 'dashboard.headerTextColor':
        elements.push(
          ...Array.from(document.querySelectorAll('p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link span'))
        );
        elements.push(...Array.from(document.querySelectorAll('.p-panel-header')));
        break;

      // Table
      // --------------------------------------------------
      case 'table.body.secondaryBackgroundColor':
        elements.push(
          ...Array.from(
            document.querySelectorAll('.p-datatable.p-datatable-striped .p-datatable-tbody>tr:nth-child(2n)')
          )
        );
        break;

      case 'table.body.primaryBackgroundColor':
        elements.push(
          ...Array.from(
            document.querySelectorAll('.p-datatable.p-datatable-striped .p-datatable-tbody>tr:nth-child(2n+1)')
          )
        );
        break;

      case 'table.body.textColor':
        elements.push(...Array.from(document.querySelectorAll('p-table tbody tr span')));
        break;

      case 'table.header.backgroundColor':
        elements.push(...Array.from(document.querySelectorAll('p-table .p-datatable .p-datatable-header')));
        elements.push(...Array.from(document.querySelectorAll('p-paginator .p-paginator-pages .p-paginator-element')));
        elements.push(
          ...Array.from(
            document.querySelectorAll('.p-datatable .p-datatable-thead > tr > th p-sorticon sortalticon svg')
          )
        );
        break;

      case 'table.header.textColor':
        elements.push(...Array.from(document.querySelectorAll('.table-header-title')));
        elements.push(...Array.from(document.querySelectorAll('p-paginator .p-paginator-pages .p-paginator-element')));
        break;

      case 'table.subHeaderBackgroundColor':
        elements.push(...Array.from(document.querySelectorAll('p-table thead th')));
        elements.push(...Array.from(document.querySelectorAll('p-paginator .p-paginator')));
        elements.push(...Array.from(document.querySelectorAll('.p-datatable .p-paginator-bottom')));
        break;

      case 'table.subHeaderTextColor':
        elements.push(...Array.from(document.querySelectorAll('.p-datatable .p-datatable-thead > tr > th')));
        elements.push(
          ...Array.from(document.querySelectorAll('p-paginator .p-paginator .p-paginator-rpp-options span'))
        );
        elements.push(...Array.from(document.querySelectorAll('p-paginator .p-paginator .p-paginator-current')));
        break;

      // Form
      // --------------------------------------------------
      case 'form.header.primaryBackgroundColor':
        elements.push(...Array.from(document.querySelectorAll('.form-card-header')));
        break;

      case 'form.header.textColor':
        elements.push(...Array.from(document.querySelectorAll('.form-card-header h1')));
        break;

      case 'form.backgroundColor':
        elements.push(...Array.from(document.querySelectorAll('.custom-form-body')));
        break;

      case 'defaultTextColor':
        elements.push(...Array.from(document.querySelectorAll('.information-form-label-wrapper')));
        elements.push(...Array.from(document.querySelectorAll('.information-view')));
        break;

      case 'form.header.itemsColor':
        elements.push(...Array.from(document.querySelectorAll('.form-header-items')));
        break;

      case 'form.header.itemsTextColor':
        elements.push(
          ...Array.from(document.querySelectorAll('.form-header-items .p-button-icon.p-button-icon-left.pi.pi-ban'))
        );

        elements.push(
          ...Array.from(
            document.querySelectorAll('.form-header-items .p-button-icon.p-button-icon-left.pi.pi-check-square')
          )
        );

        elements.push(...Array.from(document.querySelectorAll('.form-header-items .p-button-label')));
        break;

      default:
        break;
    }

    return elements;
  }

  // Backward compatibility method
  protected animatePrimaryColor() {
    this.animateColor('primary');
  }

  // Logo animation properties
  protected logoAnimationActive: boolean = false;
  protected smallLogoAnimationActive: boolean = false;
  protected faviconAnimationActive: boolean = false;

  protected startLogoAnimation() {
    if (this.isColorPickerOpened) {
      return;
    }

    this.logoAnimationActive = true;
  }

  protected stopLogoAnimation() {
    this.logoAnimationActive = false;
  }

  protected startSmallLogoAnimation() {
    if (this.isColorPickerOpened) {
      return;
    }
    this.smallLogoAnimationActive = true;
  }

  protected stopSmallLogoAnimation() {
    this.smallLogoAnimationActive = false;
  }

  protected startFaviconAnimation() {
    if (this.isColorPickerOpened) {
      return;
    }
    this.faviconAnimationActive = true;
  }

  protected stopFaviconAnimation() {
    this.faviconAnimationActive = false;
  }

  protected applyLiveColor(overrideByPrimary?: boolean) {
    this.initializeTableConfig(overrideByPrimary);
    this.applyThemeCssProperties(overrideByPrimary);
    this.setDashboardConfigs();
  }

  // Theme
  // --------------------------------------------------
  protected theme: PortalTheme = this.portalThemeService.getDefaultTheme();

  protected presetColors: Array<string> = ['#fff', '#000'];

  // Table
  // --------------------------------------------------
  protected data: ConsumerTableDisplay[] = [
    {
      ConsumerId: 1,
      NameFirst: 'John',
      NameLast: 'Doe',
      LastSeenDateTime: new Date('2024-11-01T10:30:00'),
    },
    {
      ConsumerId: 2,
      NameFirst: 'Jane',
      NameLast: 'Smith',
      LastSeenDateTime: new Date('2024-11-02T14:45:00'),
    },
    {
      ConsumerId: 3,
      NameFirst: 'Robert',
      NameLast: 'Johnson',
      LastSeenDateTime: new Date('2024-11-03T09:15:00'),
    },
    {
      ConsumerId: 4,
      NameFirst: 'Emily',
      NameLast: 'Williams',
      LastSeenDateTime: null,
    },
    {
      ConsumerId: 5,
      NameFirst: 'Michael',
      NameLast: 'Brown',
      LastSeenDateTime: new Date('2024-11-04T16:20:00'),
    },
  ];

  protected config!: EPTableConfig<ConsumerTableDisplay>;

  private initializeTableConfig(overrideByPrimary?: boolean): void {
    this.config = {
      header: 'Table',
      pagination: {
        rows: 10,
        showCurrentPageReport: true,
      },
      columns: [
        {
          header: 'First Name',
          field: 'NameFirst',
          columnType: 'string',
          defaultValue: 'VALUED',
        },
        {
          header: 'Last Name',
          field: 'NameLast',
          columnType: 'string',
          defaultValue: 'CUSTOMER',
        },
        {
          header: 'Last Seen',
          field: 'LastSeenDateTime',
          columnType: 'date',
          defaultValue: null,
        },
      ],
      style: {
        ...this.portalThemeService.getTableStyle(this.theme, {
          advancedView: this.advancedView,
          overrideByPrimary,
        }),
      },
    };
  }

  // Sidebar
  // --------------------------------------------------
  activeMenuAccordionIndex: number = 0;

  // Dashboard
  // --------------------------------------------------
  protected configs: ViewCardConfig[] = [];
  protected overviews: OverviewPanelData[] = [];

  private setDashboardConfigs(): void {
    this.configs = [
      {
        header: `$15,900`,
        headerSubtext: '',
        subheader: 'SALES',
        text: `\u00A0`,
        backgroundColor: this.theme.dashboard.sales,
        pathToImg: '',
      },
      {
        header: `5000`,
        headerSubtext: 'Enrolled',
        subheader: 'LOYALTY',
        text: `2000 Current `,
        backgroundColor: this.theme.dashboard.loyalty,
        pathToImg: '',
      },
      {
        header: '10%',
        headerSubtext: '1500 ROI',
        subheader: 'REWARDS',
        text: ' $300.00 Members Sales',
        pathToImg: '',
        backgroundColor: this.theme.dashboard.rewards,
        zoomOnHover: true,
      },
      {
        header: '40%',
        headerSubtext: '30 Average Score',
        subheader: 'REVIEWS',
        text: '30 Responses',
        pathToImg: '',
        backgroundColor: this.theme.dashboard.reviews,
        zoomOnHover: true,
      },
    ];

    this.overviews = [
      {
        header: 'TOTAL',
        subHeader: `300 Transactions`,
        body: `$5000.00`,
        bodyColor: this.theme?.dashboard.sales,
        footer: `$30 Average`,
      },
      {
        header: 'MEMBER',
        subHeader: `80%`,
        body: `$80`,
        bodyColor: this.theme?.dashboard.loyalty,
        footer: `$40 Average`,
      },
      {
        header: 'NON-MEMBER',
        subHeader: `100 Transactions`,
        body: `$2000.00`,
        bodyColor: this.theme?.dashboard.rewards ?? ColorsEnum.DOC_PURPLE,
        footer: `$10 Average`,
      },
    ];
  }

  // Logo
  // --------------------------------------------------
  protected getImageSrc() {
    return this.customLogoSrc || '/assets/enroll-and-pay-logo.png';
  }

  protected getSmallImageSrc() {
    return this.customReducedLogoSrc || '/assets/small-enroll-and-pay-logo.png';
  }

  protected getFaviconSrc() {
    return this.customFaviconSrc || '/assets/favicons/enrollAndPayFavicon.ico';
  }

  protected onLogoUpload(event: any) {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        this.customLogoUploaded = false;
        this.messageService.add({
          severity: 'error',
          summary: 'ERROR: loading image',
          detail: `Please select a valid image file (PNG, JPEG, JPG or SVG).`,
        });
        return;
      }

      // Validate file size (limit to 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        this.customLogoUploaded = false;
        this.messageService.add({
          severity: 'error',
          summary: 'ERROR: loading image',
          detail: `Please select an image file smaller than 5MB.`,
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const img = new Image();
        img.onload = () => {
          if (img.width > 200 || img.height > 70) {
            this.customLogoUploaded = false;
            this.messageService.add({
              severity: 'error',
              summary: 'ERROR: loading image',
              detail: `Image dimensions are too large. Maximum allowed size is 200x70 pixels. Your image is ${img.width}x${img.height} pixels.`,
            });
            return;
          }

          // If dimensions are valid, set the logo source
          this.customLogoSrc = e.target.result;
        };
        img.onerror = () => {
          this.customLogoUploaded = false;
          this.messageService.add({
            severity: 'error',
            summary: 'ERROR: loading image',
            detail: `Error loading image. Please try again with a valid image file.`,
          });
        };
        img.src = e.target.result;
      };
      reader.onerror = () => {
        alert('Error reading file. Please try again.');
        this.customLogoUploaded = false;
      };
      reader.readAsDataURL(file);
      this.customLogoUploaded = true;
    }

    // Reset the input value so the same file can be selected again if needed
    event.target.value = '';
  }

  protected resetLogo() {
    this.customLogoSrc = this.customLogoSrcBkp;
    this.customLogoUploaded = false;
  }

  protected onReducedLogoUpload(event: any) {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        this.messageService.add({
          severity: 'error',
          summary: 'ERROR: loading image',
          detail: `Please select a valid image file (PNG, JPEG, JPG or SVG).`,
        });
        this.customReducedLogoUploaded = false;
        return;
      }

      // Validate file size (limit to 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        this.messageService.add({
          severity: 'error',
          summary: 'ERROR: loading image',
          detail: `Please select an image file smaller than 5MB.`,
        });
        this.customReducedLogoUploaded = false;
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const img = new Image();
        img.onload = () => {
          // Validate image dimensions (max 60x70)
          if (img.width > 60 || img.height > 70) {
            this.messageService.add({
              severity: 'error',
              summary: 'ERROR: loading image',
              detail: `Image dimensions are too large. Maximum allowed size is 60x70 pixels. Your image is ${img.width}x${img.height} pixels.`,
            });
            this.customReducedLogoUploaded = false;
            return;
          }

          // If dimensions are valid, set the reduced logo source
          this.customReducedLogoSrc = e.target.result;
        };
        img.onerror = () => {
          this.messageService.add({
            severity: 'error',
            summary: 'ERROR: loading image',
            detail: `Error loading image. Please try again with a valid image file.`,
          });
          this.customReducedLogoUploaded = false;
        };
        img.src = e.target.result;
      };
      reader.onerror = () => {
        this.messageService.add({
          severity: 'error',
          summary: 'ERROR: loading image',
          detail: `Error reading file. Please try again.`,
        });
        this.customReducedLogoUploaded = false;
      };
      this.customReducedLogoUploaded = true;
      reader.readAsDataURL(file);
    }

    // Reset the input value so the same file can be selected again if needed
    event.target.value = '';
  }

  protected resetReducedLogo() {
    this.customReducedLogoSrc = this.customReducedLogoSrcBkp;
    this.customReducedLogoUploaded = false;
  }

  protected onFaviconUpload(event: any) {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type - include .ico for favicon
      const allowedTypes = [
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/gif',
        'image/svg+xml',
        'image/x-icon',
        'image/vnd.microsoft.icon',
      ];
      if (!allowedTypes.includes(file.type)) {
        this.messageService.add({
          severity: 'error',
          summary: 'ERROR: loading image',
          detail: `Please select a valid favicon file (PNG, JPEG, JPG, GIF, SVG, or ICO).`,
        });
        this.customFaviconUploaded = false;
        return;
      }

      // Validate file size (limit to 2MB for favicon)
      const maxSize = 2 * 1024 * 1024; // 2MB in bytes
      if (file.size > maxSize) {
        this.messageService.add({
          severity: 'error',
          summary: 'ERROR: loading image',
          detail: `Please select a favicon file smaller than 2MB.`,
        });
        this.customFaviconUploaded = false;
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const img = new Image();
        img.onload = () => {
          // Validate image dimensions (max 32x32)
          if (img.width > 32 || img.height > 32) {
            this.messageService.add({
              severity: 'error',
              summary: 'ERROR: loading image',
              detail: `Image dimensions are too large. Maximum allowed size is 32x32 pixels. Your image is ${img.width}x${img.height} pixels.`,
            });
            this.customFaviconUploaded = false;
            return;
          }

          // If dimensions are valid, set the favicon source
          this.customFaviconSrc = e.target.result;
        };
        img.onerror = () => {
          this.messageService.add({
            severity: 'error',
            summary: 'ERROR: loading image',
            detail: `Error loading image. Please try again with a valid image file.`,
          });
          this.customFaviconUploaded = false;
        };
        img.src = e.target.result;
      };
      reader.onerror = () => {
        this.messageService.add({
          severity: 'error',
          summary: 'ERROR: loading image',
          detail: `Error reading file. Please try again.`,
        });
        this.customFaviconUploaded = false;
      };
      this.customFaviconUploaded = true;
      reader.readAsDataURL(file);
    }

    // Reset the input value so the same file can be selected again if needed
    event.target.value = '';
  }

  protected resetFavicon() {
    this.customFaviconSrc = this.customFaviconSrcBkp;
    this.customFaviconUploaded = false;
  }

  // HEX Color Validation
  // --------------------------------------------------

  /**
   * Validates if a string is a valid HEX color code
   * @param value - The string to validate
   * @returns true if valid HEX color, false otherwise
   */
  protected isValidHexColor(value: string): boolean {
    if (!value) return false;

    // Remove any whitespace
    value = value.trim();

    // Add # if not present
    if (!value.startsWith('#')) {
      value = '#' + value;
    }

    // Check if it matches HEX pattern (3 or 6 digits after #)
    const hexPattern = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/;
    return hexPattern.test(value);
  }

  /**
   * Normalizes HEX color input by adding # if missing and converting to uppercase
   * @param value - The input value
   * @returns normalized HEX color or original value if invalid
   */
  protected normalizeHexColor(value: string): string {
    if (!value) return value;

    value = value.trim();

    // Add # if not present
    if (!value.startsWith('#')) {
      value = '#' + value;
    }

    // Convert to uppercase for consistency
    value = value.toUpperCase();

    return this.isValidHexColor(value) ? value : value;
  }

  /**
   * Handles keypress events to prevent invalid characters in HEX color inputs
   * @param event - KeyboardEvent
   */
  protected onHexColorKeyPress(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight'];
    const allowedChars = /[0-9A-Fa-f#]/;

    // Allow control keys
    if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
      return;
    }

    // Prevent invalid characters
    if (!allowedChars.test(event.key)) {
      event.preventDefault();
      return;
    }

    const target = event.target as HTMLInputElement;
    const currentValue = target.value;
    const selectionStart = target.selectionStart || 0;
    const selectionEnd = target.selectionEnd || 0;

    // Calculate what the new value would be after the keypress
    // If text is selected, it will be replaced; otherwise, character is inserted
    const beforeSelection = currentValue.substring(0, selectionStart);
    const afterSelection = currentValue.substring(selectionEnd);
    const newValue = beforeSelection + event.key + afterSelection;

    // Prevent more than 7 characters (including #)
    if (newValue.length > 7) {
      event.preventDefault();
    }
  }

  /**
   * Handles paste events to validate and clean HEX color input
   * @param event - ClipboardEvent
   */
  protected onHexColorPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const pasteData = event.clipboardData?.getData('text') || '';
    let cleanedValue = pasteData.trim();

    // Remove any non-hex characters except #
    cleanedValue = cleanedValue.replace(/[^0-9A-Fa-f#]/g, '');

    // Ensure only one # at the beginning
    if (cleanedValue.includes('#')) {
      const parts = cleanedValue.split('#');
      cleanedValue = '#' + parts.join('');
    }

    // Limit to 7 characters
    if (cleanedValue.length > 7) {
      cleanedValue = cleanedValue.substring(0, 7);
    }

    const target = event.target as HTMLInputElement;
    target.value = cleanedValue;

    // Trigger change event to update the model
    target.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * Handles input events to validate and update HEX color values
   * @param event - Event
   * @param propertyPath - The path to the theme property (e.g., 'primary', 'sidebar.textColor')
   */
  protected onHexColorInput(event: Event, propertyPath: string): void {
    const target = event.target as HTMLInputElement;
    let value = target.value;

    // Normalize the value
    value = this.normalizeHexColor(value);

    // Update the input value if it was normalized
    if (target.value !== value) {
      target.value = value;
    }

    // Update the theme property if valid
    if (this.isValidHexColor(value)) {
      this.setNestedProperty(this.theme, propertyPath, value);
      this.applyLiveColor();

      // Remove any error styling
      target.classList.remove('invalid-hex');
    } else {
      // Add error styling for invalid input
      target.classList.add('invalid-hex');
    }
  }

  /**
   * Helper method to get nested object properties using dot notation
   * @param obj - The object to read from
   * @param path - The property path (e.g., 'sidebar.backgroundColor.primary')
   * @returns The value at the specified path
   */
  private getNestedProperty(obj: any, path: string): any {
    const keys = path.split('.');
    let current = obj;

    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return undefined;
      }
    }

    return current;
  }

  /**
   * Helper method to set nested object properties using dot notation
   * @param obj - The object to modify
   * @param path - The property path (e.g., 'sidebar.backgroundColor.primary')
   * @param value - The value to set
   */
  private setNestedProperty(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      if (!(keys[i] in current)) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }

    current[keys[keys.length - 1]] = value;
  }
}
